import classNames from 'classnames'
import React from 'react'

import Locale from 'data/locale'
import Store from 'store'
import EditingContext from 'store/editingContext'
import Session from 'store/session'

import {
  faCaretRight,
  faFolder,
  faFolderOpen,
  faFolderPlus,
  faPencilAlt,
  faSave,
  faSpinner,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useApi from 'api'
import { getProfilePath } from 'common'
import LoginLine from 'components/account/LoginLine'
import { useImportId } from 'components/importTracker'
import { stateDeleteImports, stateSetImported } from 'logic/stateReducers'
import { useDrag, useDrop } from 'react-dnd'
import scrollIntoView from 'scroll-into-view-if-needed'
import Resizable from 'ui/resizable'
import Tooltip from 'ui/tooltip'
import { OverlayContext } from 'ui/utils/OverlayManager'
import { VirtualList } from 'ui/virtual'
import { chainedRef } from 'utils'
import { ProfileLink } from './SearchResults'
import useLoadProfile, { exportProfile } from './useLoadProfile'

const _L = Locale('ui-import')

export function foldersFix(folders) {
  // update to new format and fix circular dependencies
  let changed = false
  const result = {}

  const mfp = [0]
  const mfs = [1]

  function mf_find(x) {
    let p = mfp[x]
    while (p !== x) {
      const gp = (mfp[x] = mfp[p])
      x = p
      p = gp
    }
    return x
  }
  function mf_merge(x, y) {
    x = mf_find(x)
    y = mf_find(y)
    if (x === y) return
    const xs = mfs[x],
      ys = mfs[y]
    if (xs < ys) {
      mfp[x] = y
      mfs[y] += xs
    } else {
      mfp[y] = x
      mfs[x] += ys
    }
  }

  for (const id in folders) {
    const numeric = parseInt(id)
    if (!folders[id] || isNaN(numeric)) {
      // blank or broken entry, remove
      changed = true
      continue
    }
    let parent = 0
    if (typeof folders[id] !== 'object') {
      // old format, update
      changed = true
      result[id] = { name: folders[id], parent }
    } else {
      parent = parseInt(folders[id].parent)
      if (isNaN(parent)) {
        changed = true
        parent = 0
        result[id] = { name: folders[id].name, parent }
      } else {
        result[id] = folders[id]
      }
    }
    mfp[numeric] = numeric
    mfs[numeric] = 1
  }

  for (const id in result) {
    mf_merge(parseInt(id), result[id].parent)
  }

  const root = mf_find(0)
  for (const id in result) {
    const group = mf_find(parseInt(id))
    if (group !== root) {
      changed = true
      result[id].parent = 0
    }
  }

  return changed ? result : folders
}

function folderIsChild(folders, id, parent) {
  for (let cur = id; cur; cur = folders[cur].parent) {
    if (cur === parent) {
      return true
    }
  }
  return false
}

function folderSetParent(folders, id, parent) {
  if (folders[id].parent === parent || folderIsChild(folders, parent, id)) {
    return folders
  }
  folders = { ...folders }
  folders[id] = { ...folders[id], parent }
  return folders
}

function folderRename(folders, id, name) {
  if (!name || folders[id].name === name) return folders
  folders = { ...folders }
  folders[id] = { ...folders[id], name }
  return folders
}

function folderDelete(folders, id) {
  folders = { ...folders }
  const parent = folders[id].parent
  for (const other in folders) {
    if (folders[other].parent === id) {
      folders[other] = { ...folders[other], parent }
    }
  }
  delete folders[id]
  return folders
}

function profileUpdate(profiles, id, charClass) {
  const index = profiles?.findIndex((profile) => profile.id === id)
  if (!profiles || index < 0) return profiles
  profiles = profiles.slice()
  profiles[index] = {
    ...profiles[index],
    class: charClass,
    date: Math.floor(Date.now() / 1000)
  }
  return profiles
}

function profileDelete(profiles, id) {
  const index = profiles?.findIndex((profile) => profile.id === id)
  if (!profiles || index < 0) return profiles
  profiles = profiles.slice()
  profiles.splice(index, 1)
  return profiles
}

function profileRename(profiles, id, name) {
  const index = profiles?.findIndex((profile) => profile.id === id)
  if (!profiles || index < 0 || profiles[index].value === name) return profiles
  profiles = profiles.slice()
  profiles[index] = { ...profiles[index], value: name }
  return profiles
}

function profileMove(profiles, id, folder) {
  const index = profiles?.findIndex((profile) => profile.id === id)
  if (!profiles || index < 0) return profiles
  profiles = profiles.slice()
  profiles[index] = { ...profiles[index], folder }
  return profiles
}

function TextEdit({ value, onChange }) {
  const ref = React.useRef()
  React.useEffect(() => {
    ref.current.select()
    ref.current.focus()
  }, [])
  const onBlur = React.useCallback(() => {
    onChange(ref.current.value)
  }, [onChange])
  const onKeyDown = React.useCallback(
    (e) => {
      switch (e.key) {
        case 'Enter':
          ref.current.blur()
          break
        case 'Esc':
        case 'Escape':
          onChange(null)
          break
        // no default
      }
    },
    [onChange]
  )
  const onClick = React.useCallback((e) => {
    e.stopPropagation()
    e.preventDefault()
  }, [])
  return (
    <input
      defaultValue={value}
      ref={ref}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onClick={onClick}
    />
  )
}

function ProfileFolderCreate({ dispatch, folders }) {
  const editDispatch = EditingContext.useDispatch()

  const onClick = React.useCallback(() => {
    let id = 1
    while (folders[id]) {
      id += 1
    }
    const folder = { name: _L`New Folder`, parent: 0 }
    dispatch.setFolders((folders) => ({ ...folders, [id]: folder }))
    editDispatch({ type: 'folder', id })
  }, [dispatch, editDispatch, folders])

  return (
    <li>
      <span className="d2p-create" onClick={onClick}>
        <FontAwesomeIcon icon={faFolderPlus} /> {_L`New Folder`}
      </span>
    </li>
  )
}

const ProfileFolderDelete = React.memo(function ProfileFolderDelete({
  id,
  setActive
}) {
  const sessionStore = Session.useStore()
  const overlays = React.useContext(OverlayContext)
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const dispatch = Session.useDispatch()
  const mainDispatch = Store.useDispatch()

  const onClick = React.useCallback(
    async (e) => {
      e.persist()
      const store = sessionStore.getState()
      const parent = store.folders?.[id]?.parent || 0
      if (!store.profiles || store.profiles.find((p) => p.folder === id)) {
        const result = await overlays.menu(e, [
          _L`Move all profiles to parent folder`,
          _L`Delete all profiles`
        ])
        try {
          if (result === 0) {
            await api.folders('moveall', { from: id, to: parent })
            dispatch.setProfiles((profiles) =>
              profiles.map((p) =>
                p.folder === id ? { ...p, folder: parent } : p
              )
            )
          } else if (result === 1) {
            await api.folders('delall', { from: id })
            dispatch.setProfiles((profiles) =>
              profiles.filter((p) => p.folder !== id)
            )
            mainDispatch.setState((state) =>
              stateDeleteImports(
                state,
                ...store.profiles
                  .filter((p) => p.folder === id)
                  .map((p) => p.id)
              )
            )
          } else {
            return
          }
        } catch (error) {
          setError(error.message)
          return
        }
      }

      dispatch.setFolders((folders) => folderDelete(folders, id))
      setActive(parent)
    },
    [
      sessionStore,
      overlays,
      setError,
      api,
      dispatch,
      mainDispatch,
      setActive,
      id
    ]
  )

  return (
    <span className="d2p-modify" onClick={onClick}>
      <FontAwesomeIcon icon={faTrashAlt} />
    </span>
  )
})

function ProfileFolder({ id, ...props }) {
  const { folders, subfolders, counts, active, setActive, dispatch } = props
  const [editing, setEditing] = React.useState(false)
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')

  const isActive = active === id
  const onClick = React.useCallback(() => setActive(id), [setActive, id])

  const api = useApi()

  const [{ dragging }, drag] = useDrag({
    type: 'FOLDER',
    item: { type: 'FOLDER', id },
    canDrag: () => id !== 0 && !editing,
    collect: (monitor) => ({ dragging: monitor.isDragging() })
  })
  const [{ draggingSomething, dropping }, drop] = useDrop({
    accept: ['FOLDER', 'PROFILE'],
    canDrop: (item) => {
      if (item.type === 'FOLDER') {
        return (
          item.id &&
          folders[item.id].parent !== id &&
          !folderIsChild(folders, id, item.id)
        )
      } else if (item.type === 'PROFILE') {
        return item.folder !== id
      }
    },
    drop: (item) => {
      if (item.type === 'FOLDER') {
        dispatch.setFolders((folders) => folderSetParent(folders, item.id, id))
      } else if (item.type === 'PROFILE') {
        dispatch.setProfiles((profiles) => profileMove(profiles, item.id, id))
        setStatus(_L`Moving...`)
        setError(null)
        api
          .folders('move', { id: item.id, to: id })
          .catch((error) => {
            dispatch.setProfiles((profiles) =>
              profileMove(profiles, id, item.id)
            )
            setError(error.message)
          })
          .finally(() => setStatus(null))
      }
    },
    collect: (monitor) => ({
      dropping: monitor.canDrop() && monitor.isOver(),
      draggingSomething: ['FOLDER', 'PROFILE'].includes(monitor.getItemType())
    })
  })

  const onEdit = React.useCallback((e) => {
    e.stopPropagation()
    setEditing(true)
  }, [])
  const onEditChange = React.useCallback(
    (name) => {
      setEditing(false)
      if (name) {
        dispatch.setFolders((folders) => folderRename(folders, id, name))
      }
    },
    [dispatch, id]
  )

  const lineRef = React.useRef()
  const editingCallback = React.useCallback(
    ({ type, id: targetId }) => {
      if (type === 'folder' && id === targetId) {
        scrollIntoView(lineRef.current, {
          scrollMode: 'if-needed',
          block: 'center'
        })
        setEditing(true)
        return true
      }
    },
    [id]
  )
  EditingContext.useHandler(editingCallback)

  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop])

  const highlight = dropping || (isActive && !draggingSomething)

  return (
    <li ref={lineRef}>
      <div
        className={classNames({
          'd2p-active': highlight,
          'd2p-dragging': dragging,
          'd2p-root': id === 0,
          'd2p-empty': counts && !counts[id]
        })}>
        <span
          className="d2p-label"
          onClick={isActive ? null : onClick}
          ref={nodeRef}
          title={counts ? _L`${counts[id] || 0} profiles` : null}>
          <span className="d2p-icon">
            <FontAwesomeIcon icon={isActive ? faFolderOpen : faFolder} />
          </span>
          {id ? (
            editing ? (
              <TextEdit value={folders[id].name} onChange={onEditChange} />
            ) : (
              folders[id].name
            )
          ) : (
            _L`My Profiles`
          )}
        </span>
        {!!(id !== 0 && !draggingSomething && !editing) && (
          <span className="d2p-modify" onClick={onEdit}>
            <FontAwesomeIcon icon={faPencilAlt} />
          </span>
        )}
        {!!(id !== 0 && !draggingSomething && !editing) && (
          <ProfileFolderDelete id={id} setActive={setActive} />
        )}
      </div>
      {!!subfolders[id] && (
        <ul>
          {id === 0 && (
            <ProfileFolderCreate dispatch={dispatch} folders={folders} />
          )}
          {subfolders[id].map((sub) => (
            <ProfileFolder key={sub} id={sub} {...props} />
          ))}
        </ul>
      )}
    </li>
  )
}

const ProfileFolders = React.memo(function ProfileFolders({
  folders,
  profiles,
  ...props
}) {
  const subfolders = React.useMemo(() => {
    const subfolders = {}
    subfolders[0] = []
    for (const id in folders) {
      const parent = folders[id].parent
      subfolders[parent] = subfolders[parent] || []
      subfolders[parent].push(parseInt(id))
    }
    for (const id in subfolders) {
      subfolders[id].sort((a, b) =>
        folders[a].name.localeCompare(folders[b].name)
      )
    }
    return subfolders
  }, [folders])
  const counts = React.useMemo(() => {
    if (!profiles) return null
    const counts = {}
    for (const profile of profiles) {
      counts[profile.folder] = (counts[profile.folder] || 0) + 1
    }
    return counts
  }, [profiles])
  if (!subfolders[0]) return null
  return (
    <ProfileFolder
      id={0}
      folders={folders}
      subfolders={subfolders}
      counts={counts}
      {...props}
    />
  )
})

function ProfileLine({ hero, active, onLoad, onSave, onDelete, onRename }) {
  const overlays = React.useContext(OverlayContext)
  const [renaming, setRenaming] = React.useState(false)
  const setStatus = EditingContext.useStateSetter('profilesStatus')

  const [, drag] = useDrag({
    type: 'PROFILE',
    item: { type: 'PROFILE', id: hero.id, folder: hero.folder }
  })

  const onSaveClick = React.useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      overlays
        .menu(e, [_L`Save all sets`, _L`Save current set`, _L`Save mercenary`])
        .then((result) => {
          if (result != null)
            onSave(hero.id, hero.value, ['all', 'current', 'mercenary'][result])
        })
    },
    [overlays, hero, onSave]
  )

  const onRenameClick = React.useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setStatus(
        _L`Due to technical limitations, renaming a profile only changes its name in the list - you need to save over it to update the page title.`
      )
      setRenaming(true)
    },
    [setStatus]
  )
  const onRenameChange = React.useCallback(
    (name) => {
      setRenaming(false)
      setStatus(null)
      if (name !== hero.value && name != null) {
        onRename(hero, name)
      }
    },
    [hero, onRename, setStatus]
  )

  const onDeleteClick = React.useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      overlays.menu(e, [_L`Confirm delete?`]).then((result) => {
        if (result === 0) onDelete(hero)
      })
    },
    [overlays, hero, onDelete]
  )

  return (
    <li
      className={classNames({
        'd2p-active': active,
        'd2p-renaming': renaming
      })}>
      <ProfileLink id={hero.id} onClick={onLoad} ref={renaming ? null : drag}>
        <span
          className={classNames(
            `d2p-class-icon d2p-class-${hero.class}`,
            hero.gender && `d2p-${hero.gender}`
          )}>
          &nbsp;
        </span>
        <span
          className={classNames('d2p-profile-name', {
            'd2p-unnamed': !hero.value
          })}>
          {renaming ? (
            <TextEdit value={hero.value || ''} onChange={onRenameChange} />
          ) : (
            hero.value || _L`Unnamed profile`
          )}
        </span>
      </ProfileLink>
      <span className="d2p-profile-saved">
        <span className="d2p-modify">
          <span onClick={onRenameClick} title={_L`Rename profile`}>
            <FontAwesomeIcon icon={faPencilAlt} />
          </span>
          <span onClick={onSaveClick} title={_L`Update profile`}>
            <FontAwesomeIcon icon={faSave} />
          </span>
          <span onClick={onDeleteClick} title={_L`Delete profile`}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </span>
        </span>
        <span className="d2p-date">
          {new Date(1000 * hero.date).toLocaleString()}
        </span>
      </span>
    </li>
  )
}

function useUpdateProfile() {
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const mainStore = Store.useStore()
  const mainDispatch = Store.useDispatch()
  const sessionStore = Session.useStore()
  const sessionDispatch = Session.useDispatch()
  const userId = Session.useSelector((state) => state.userId)
  return React.useCallback(
    (id, name, saveMode) => {
      const state = mainStore.getState()
      const data = exportProfile(state, name, saveMode)
      const exportedId =
        saveMode === 'current' ? state.profiles[state.activeProfile].id : null
      setError(null)
      setStatus(_L`Saving...`)
      const profile = sessionStore.getState().profiles?.find((p) => p.id === id)
      if (profile) {
        sessionDispatch.setProfiles((profiles) =>
          profileUpdate(profiles, id, data.class)
        )
      }
      return api.save(data, { id: id }).then(
        (id) => {
          const url = new URL(getProfilePath(id), window.location)
          setStatus(url.href)
          if (saveMode !== 'mercenary') {
            mainDispatch.setState((state) =>
              stateSetImported(state, id, userId, exportedId)
            )
          }
        },
        (error) => {
          // restore profile
          if (profile) {
            sessionDispatch.setProfiles((profiles) => {
              const index = profiles?.findIndex((p) => p.id === id)
              if (!profiles || index < 0) return profiles
              profiles = profiles.slice()
              profiles[index] = profile
              return profiles
            })
          }
          setStatus(null)
          setError(error.message)
        }
      )
    },
    [
      api,
      mainStore,
      setStatus,
      setError,
      sessionStore,
      sessionDispatch,
      mainDispatch,
      userId
    ]
  )
}

export const ProfileUpdateButton = React.memo(function ProfileUpdateButton() {
  const userId = Session.useSelector((state) => state.userId)
  const saveActiveId = Store.useProfile(
    (profile) =>
      userId &&
      profile.imported !== true &&
      profile.user === userId &&
      profile.importedId
  )
  const saveStateId = Store.useSelector(
    (state) =>
      userId &&
      state.imported !== true &&
      state.user === userId &&
      state.importedId
  )
  const setSpinner = EditingContext.useStateSetter('spinner')

  const onSave = useUpdateProfile()
  const onClick = React.useCallback(() => {
    if (saveActiveId) {
      setSpinner((spin) => spin + 1)
      onSave(saveActiveId, null, 'current').finally(() =>
        setSpinner((spin) => spin - 1)
      )
    } else if (saveStateId) {
      setSpinner((spin) => spin + 1)
      onSave(saveStateId).finally(() => setSpinner((spin) => spin - 1))
    }
  }, [onSave, saveActiveId, saveStateId, setSpinner])

  if (!saveActiveId && !saveStateId) return null
  return (
    <Tooltip
      tooltip={saveActiveId ? _L`Update current set` : _L`Update profile`}>
      <FontAwesomeIcon
        icon={faSave}
        className="d2p-ProfileUpdateButton"
        onClick={onClick}
      />
    </Tooltip>
  )
})

const ProfileList = React.memo(function ProfileList({ profiles, folder }) {
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const dispatch = Session.useDispatch()
  const mainDispatch = Store.useDispatch()
  const currentProfile = useImportId()

  const loadProfile = useLoadProfile()

  const onSave = useUpdateProfile()

  const onDelete = React.useCallback(
    (profile) => {
      dispatch.setProfiles((profiles) => profileDelete(profiles, profile.id))
      setError(null)
      setStatus(_L`Deleting...`)
      api
        .delete(profile.id)
        .then(
          () => {
            mainDispatch.setState((state) =>
              stateDeleteImports(state, profile.id)
            )
          },
          (error) => {
            dispatch.setProfiles(
              (profiles) => profiles && [...profiles, profile]
            )
            setError(error.message)
          }
        )
        .finally(() => setStatus(null))
    },
    [api, dispatch, mainDispatch, setStatus, setError]
  )

  const onRename = React.useCallback(
    (profile, name) => {
      dispatch.setProfiles((profiles) =>
        profileRename(profiles, profile.id, name)
      )
      setError(null)
      setStatus(_L`Renaming...`)
      api
        .folders('rename', { id: profile.id, name })
        .catch((error) => {
          dispatch.setProfiles((profiles) =>
            profileRename(profiles, profile.id, profile.value)
          )
          setError(error.message)
        })
        .finally(() => setStatus(null))
    },
    [api, dispatch, setStatus, setError]
  )

  const filtered = React.useMemo(() => {
    return (
      profiles &&
      profiles
        .filter((profile) => folder < 0 || profile.folder === folder)
        .sort((a, b) => b.date - a.date)
    )
  }, [profiles, folder])

  const renderProfile = React.useCallback(
    (profile) => (
      <ProfileLine
        key={profile.id}
        hero={profile}
        active={currentProfile === profile.id?.toString()}
        onLoad={loadProfile}
        onSave={onSave}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    [currentProfile, loadProfile, onDelete, onSave, onRename]
  )

  if (!profiles) {
    return (
      <div className="d2p-profiles">
        {!profiles && (
          <li className="d2p-loading">
            <FontAwesomeIcon icon={faSpinner} spin /> {_L`Loading...`}
          </li>
        )}
      </div>
    )
  }

  return (
    <VirtualList
      className="d2p-profiles"
      items={filtered}
      renderItem={renderProfile}
      maxHeight={400}
      itemHeight={20}
    />
  )
})

function ProfilesView() {
  const [status, setStatus] = EditingContext.useState('profilesStatus')
  const [error, setError] = EditingContext.useState('profilesError')
  const [currentFolder, setCurrentFolder] = EditingContext.useState(
    'profilesCurrentFolder',
    0
  )
  const folders = Session.useSelector((store) => store.folders)
  const profiles = Session.useSelector((store) => store.profiles)
  const dispatch = Session.useDispatch()

  const [showFolders, setShowFolders] = EditingContext.useState(
    'profilesShowFolders',
    2
  )
  const timeoutRef = React.useRef()
  const onToggleFolders = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (showFolders === 0) {
      setShowFolders(1)
      timeoutRef.current = setTimeout(() => setShowFolders(2), 250)
    } else {
      setShowFolders(0)
    }
  }, [showFolders, setShowFolders])

  const api = useApi()

  // load/save folders hook
  const foldersPrev = React.useRef(false) // we use a value that can't be equal to any actual state
  React.useEffect(() => {
    if (foldersPrev.current === false) {
      if (!folders) {
        // initial load
        api.folders().then(
          (result) => {
            foldersPrev.current = result
            dispatch.setFolders(foldersFix(result))
          },
          (error) => setError(error.message)
        )
      }
    } else if (foldersPrev.current !== folders) {
      if (!folders) {
        // we already loaded, but dispatch didn't go through yet
        return
      }
      // folders changed through user action
      setStatus(_L`Saving...`)
      setError(null)
      api
        .folders('save', folders)
        .catch((error) => setError(error.message))
        .finally(() => setStatus(null))
    }
    foldersPrev.current = folders
  }, [api, dispatch, folders, setStatus, setError])

  // load profiles hook
  const profilesLoaded = React.useRef(false)
  React.useEffect(() => {
    if (profilesLoaded.current) return
    profilesLoaded.current = true
    if (!profiles) {
      setStatus(_L`Loading...`)
      setError(null)
      api
        .search()
        .then(
          (result) => dispatch.setProfiles(result),
          (error) => setError(error.message)
        )
        .finally(() => setStatus(null))
    }
  }, [api, dispatch, profiles, setStatus, setError])

  const statusRef = React.useRef()
  const onStatusFocus = React.useCallback(() => {
    statusRef.current.select()
  }, [])

  return (
    <div className="d2p-MyProfiles">
      {!!(folders || profiles) && (
        <div className="d2p-profiles-view">
          <Resizable
            className={classNames('d2p-folders', {
              'd2p-collapsed': showFolders === 0,
              'd2p-expanding': showFolders === 1
            })}
            side={showFolders === 2 ? 'e' : ''}>
            <ul className="d2p-folder-list">
              {!folders && (
                <li className="d2p-loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> {_L`Loading...`}
                </li>
              )}
              {!!folders && (
                <ProfileFolders
                  folders={folders}
                  profiles={profiles}
                  active={currentFolder}
                  setActive={setCurrentFolder}
                  dispatch={dispatch}
                />
              )}
            </ul>
            <div className="d2p-toggle" onClick={onToggleFolders}>
              <FontAwesomeIcon icon={faCaretRight} />
            </div>
          </Resizable>
          <ProfileList
            profiles={profiles}
            folder={showFolders === 2 ? currentFolder : -1}
          />
        </div>
      )}
      <div className="d2p-status">
        {!!(error || status) && (
          <textarea
            type="text"
            readOnly
            value={error || status}
            ref={statusRef}
            onFocus={onStatusFocus}
          />
        )}
      </div>
    </div>
  )
}

export default function ProfilesSection() {
  const loggedIn = Session.useSelector((store) => !!store.userName)

  if (loggedIn) {
    return <ProfilesView />
  } else {
    return (
      <div className="d2p-SectionContent">
        <LoginLine suffix={_L` to track your saved profiles`} />
      </div>
    )
  }
}
